import {
  ProviderUnavailableError,
  type ImageUpscaleProvider,
  type ImageUpscaleRequest,
  type ImageUpscaleResult,
  type ImageUpscaleVariant,
  type ProviderCallOptions,
  type ProviderImageUpscaleJobState,
  type ProviderJobHandle,
} from "@loumy/contracts";

// §7 `image-upscale`-Provider → **Real-ESRGAN** über die fal.ai-Queue-API (D163): treue
// Super-Resolution eines EINZELBILDS (kein Prompt, kein Halluzinieren) — Quell-Bild rein,
// hochskaliertes Bild raus. Versorgt das eigenständige `image-upscale`-Tool. Gleicher
// FAL_API_KEY wie die übrigen fal-Adapter. COGS ~$0.01–0.02/Bild (§10/D150-Pricing: 2 cr
// kostendeckend, wie image-bg-remove).
//
// Modell-Contract `fal-ai/esrgan` (fal-Doku verifiziert 2026-06-09):
//   INPUT: image_url (REQUIRED), scale (float, Default 2), model (enum, Default
//   RealESRGAN_x4plus), output_format (png|jpeg, Default png). OUTPUT:
//   { image: { url, content_type, width, height, file_size } }.
//
// VARIANTE → ESRGAN-Netz (D163): `general` = RealESRGAN_x4plus (Foto/3D), `anime` =
// RealESRGAN_x4plus_anime_6B (saubere Kanten für Sprites/Pixel-Art). Beide sind native 4×-
// Netze; der `scale`-Param skaliert den Output auf den gewünschten Faktor (2 = Downscale des
// 4×-Ergebnisses, der etablierte Real-ESRGAN-`outscale`-Hebel).
//
// D27 (generate/poll): generate() submittet NUR → Handle (status_url/response_url in raw);
// poll() ist EIN Status-GET, bei COMPLETED das hochskalierte Bild laden (URL — die Capability
// persistiert es wie ein gen-Bild). Resume-fähig: ein erschöpftes Tick-Budget pollt dasselbe
// Handle weiter (kein Re-Submit, keine doppelten fal-COGS).

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_ESRGAN_MODEL = "fal-ai/esrgan";

/** D163: abstrakte Variante → konkretes Real-ESRGAN-Netz (am fal-Endpoint). */
const ESRGAN_VARIANT_MODEL: Record<ImageUpscaleVariant, string> = {
  general: "RealESRGAN_x4plus",
  anime: "RealESRGAN_x4plus_anime_6B",
};

/**
 * poll()-intern (Codex-P1-Analog zu Bria/BEN2): ein TRANSIENTER Fehler beim Ergebnis-Fetch NACH
 * `COMPLETED` (fal-Result-GET 429/5xx) → poll() meldet `processing` (Retry) statt den fertigen
 * Job zu killen.
 */
class EsrganResultTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EsrganResultTransientError";
  }
}

export interface FalEsrganConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/esrgan`). */
  model?: string;
}

/**
 * §7 image-upscale → Real-ESRGAN über fal.ai (D163, D27-Handle). `generate({imageUrl, scale,
 * variant})` → submit → Handle; `poll(handle)` → Status-GET, bei COMPLETED das hochskalierte
 * Bild (URL) → done.
 */
export class FalEsrganProvider implements ImageUpscaleProvider {
  readonly id = "fal-esrgan";
  readonly version = 1;
  readonly displayName = "fal.ai (Real-ESRGAN)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalEsrganConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_ESRGAN_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("image-upscale", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal esrgan: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal esrgan: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal esrgan: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /**
   * Ergebnis-URL gegen die fal-CDN-Hosts pinnen (Codex-P1-Analog zu Bria/BEN2/VACE): die Bild-URL
   * wird als image.sourceUrl durchgereicht und stromabwärts gefetcht/persistiert — die Quelle MUSS
   * fal sein (fal.media/fal.run/fal.ai inkl. Subdomains), kein beliebiger Provider-Response-Host (SSRF).
   */
  private requireFalCdnUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal esrgan: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase().replace(/\.+$/, "");
    } catch {
      throw new Error(`fal esrgan: '${field}' ist keine gültige URL.`);
    }
    const allowed = ["fal.media", "fal.run", "fal.ai"].some((d) => host === d || host.endsWith(`.${d}`));
    if (!allowed) throw new Error(`fal esrgan: '${field}'-Host '${host}' liegt nicht auf einem fal-CDN-Host.`);
    return url;
  }

  /** COMPLETED → hochskaliertes Bild (URL). Transienter Result-Flap (429/5xx) → EsrganResultTransientError. */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<ImageUpscaleResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new EsrganResultTransientError(`fal esrgan result: HTTP ${resultRes.status} (transient)`);
      const body = (await resultRes.text().catch(() => "")).slice(0, 500);
      throw new Error(`fal esrgan result: HTTP ${resultRes.status} ${resultRes.statusText}${body ? ` — ${body}` : ""}`);
    }
    const result = (await resultRes.json()) as { image?: { url?: unknown } };
    if (typeof result.image?.url !== "string" || result.image.url.length === 0)
      throw new Error("fal esrgan: COMPLETED, aber kein image.url im Ergebnis.");
    // Codex-P1: Ergebnis-URL host-gepinnt (fal-CDN) — sie wird als sourceUrl durchgereicht und
    // stromabwärts gefetcht (kein beliebiger Provider-Response-Host, SSRF/Drift).
    const url = this.requireFalCdnUrl(result.image.url, "image.url");
    return {
      imageUrl: url,
      provenance: { provider: this.id, modelVersion: this.model, requestId, seed: null },
    };
  }

  /** D27: NUR submitten → Handle. `opts.signal` bricht ab. */
  async generate(req: ImageUpscaleRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    const model = ESRGAN_VARIANT_MODEL[req.variant] ?? ESRGAN_VARIANT_MODEL.general;
    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({ image_url: req.imageUrl, scale: req.scale, model, output_format: "png" }),
      signal: opts?.signal,
    });
    if (!submit.ok) {
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal esrgan submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal esrgan submit: keine request_id in der Antwort.");
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");
    return { providerId: this.id, providerVersion: this.version, externalId: sub.request_id, raw: { statusUrl, responseUrl } };
  }

  /** D27: EIN Status-GET. IN_QUEUE→pending, IN_PROGRESS→processing, COMPLETED→Bild→done. */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageUpscaleJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown };
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal esrgan status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal esrgan: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, handle.externalId, signal) };
        } catch (e) {
          if (e instanceof EsrganResultTransientError) return { status: "processing" };
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal esrgan: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }
}
